'use client';

import * as React from 'react';
import { defaultAuthorAtom } from 'app/blog/[domain]/_components/design-state';
import { PostAvatar } from 'app/blog/[domain]/(standalone)/_components/post-avatar';
import type { BlogPost } from 'app/blog/[domain]/(standalone)/_server/types';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { PostBreadcrumb } from './breadcrumb';
import { CoverImage } from './cover-image';
import { PostDecorator } from './decorator';
import { Excerpt } from './excerpt';
import { PublishedDate } from './published-date';
import { Sharing } from './sharing';

export interface PostHeaderProps {
  post: BlogPost;
  postUrl: string;
}

export function PostHeader({ post, postUrl }: PostHeaderProps): JSX.Element {
  const t = useTranslations('Blog.FeaturedPost');
  const author = useAtomValue(defaultAuthorAtom);
  return (
    <div className="post-header">
      <PostBreadcrumb title={post.title} />
      <PostDecorator readingTime={post.readingTime} tag={post.tags[0]} />
      <h1
        className="post-title"
        dangerouslySetInnerHTML={{ __html: post.titleHtml }}
      />
      <Excerpt excerpt={post.excerpt} />
      <CoverImage src={post.coverImage} />
      <div className="post-header-meta">
        <div className="post-byline">
          <div className="post-written-by" id="writtenBy">
            <p className="post-meta-label">{t('WrittenBy')}</p>
            <PostAuthor />
          </div>
          <PublishedDate {...post} hasTitle={!!author.title} />
        </div>
        <Sharing {...post} url={postUrl} />
      </div>
    </div>
  );
}

function PostAuthor() {
  const author = useAtomValue(defaultAuthorAtom);
  return (
    <section className="post-author" id="author">
      <PostAvatar
        name={author.name}
        avatar={author.avatar}
        className="post-author-avatar"
      />
      <div className="post-author-details">
        <p className="post-author-name" title={author.name}>
          {author.name}
        </p>
        <p className="post-author-title" title={author.title}>
          {author.title}
        </p>
      </div>
    </section>
  );
}
